// 📄 pages/Ariba/sections/GuiasIntegracionToolbox.tsx
// Acceso directo (standalone, sin projectId) al catálogo de Guías de
// Integración Estándar desde el Ariba Toolbox — mismo catálogo global de
// solo lectura que se resuelve automáticamente dentro de cada proyecto
// (ver GuiaIntegracionCard.tsx), pero acá sin progreso (no hay un proyecto
// puntual del cual colgar el checklist) — es para consulta/planeación
// antes de tener el proyecto armado. Mismo patrón que BadiCatalogToolbox.tsx.
import { useEffect, useState } from 'react';
import { RefreshCw, BookOpenCheck, Info, ChevronDown, ChevronUp } from 'lucide-react';
import Breadcrumbs from '../../../components/ui/Breadcrumbs.tsx';
import { GuiaIntegracion, getGuiasIntegracion } from '../../../services/guiasIntegracionService.ts';
import { SOLUCION_OPTIONS } from '../../Proyectos/solucionesConfig.ts';
import { labelTipoErp } from '../../Proyectos/erpConfig.ts';

interface GuiasIntegracionToolboxProps {
  setPage?: (page: string) => void;
}

const labelSolucion = (slug: string): string =>
  SOLUCION_OPTIONS.find((s) => s.value === slug)?.label || slug;

function GuiaCard({ guia }: { guia: GuiaIntegracion }) {
  const [abierto, setAbierto] = useState(false);
  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
      <button
        type="button"
        onClick={() => setAbierto((v) => !v)}
        className="w-full flex items-center justify-between gap-4 p-4 text-left hover:bg-white/[0.03] transition-all"
      >
        <div>
          <span className="inline-flex items-center gap-1.5 bg-indigo-500/10 text-indigo-300 border border-indigo-500/20 px-2.5 py-1 rounded-full text-[11px] font-semibold">
            {labelTipoErp(guia.erpTipo)}
          </span>
          <h4 className="text-white font-medium mt-2">{guia.nombre}</h4>
        </div>
        {abierto ? <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />}
      </button>
      {abierto && (
        <div className="border-t border-white/10 p-4 space-y-2">
          {guia.descripcion && <p className="text-sm text-gray-400 mb-2">{guia.descripcion}</p>}
          <ol className="space-y-2">
            {guia.pasos.map((paso, i) => (
              <li key={paso.id} className="flex items-start gap-2">
                <span className="text-xs font-mono text-gray-500 mt-0.5">{i + 1}.</span>
                <div>
                  <p className="text-sm text-white">{paso.titulo}</p>
                  {paso.descripcion && <p className="text-xs text-gray-400 mt-0.5">{paso.descripcion}</p>}
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}

export default function GuiasIntegracionToolbox({ setPage }: GuiasIntegracionToolboxProps) {
  const [guias, setGuias] = useState<GuiaIntegracion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getGuiasIntegracion()
      .then(setGuias)
      .catch((err) => { console.error('Error cargando guías de integración:', err); setError('No se pudieron cargar las guías.'); })
      .finally(() => setLoading(false));
  }, []);

  const porSolucion = guias.reduce<Record<string, GuiaIntegracion[]>>((acc, g) => {
    (acc[g.solucion] = acc[g.solucion] || []).push(g);
    return acc;
  }, {});

  return (
    <main className={setPage ? 'min-h-screen p-4 md:p-8 text-white' : 'text-white'}>
      {setPage && (
        <div className="max-w-6xl mx-auto mb-6">
          <Breadcrumbs currentPage="guias-integracion-toolbox" setPage={setPage} />
        </div>
      )}

      <div className="max-w-4xl mx-auto space-y-6">
        <header>
          <h1 className="text-2xl font-light text-white">Guías de Integración Estándar</h1>
          <p className="text-sm text-gray-400 mt-1">Checklist de configuración por combinación Solución + ERP, mantenido desde el panel de Administración.</p>
        </header>

        <div className="bg-blue-500/10 border border-blue-500/20 text-blue-300 p-3 rounded-lg text-xs flex items-start gap-2">
          <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>Dentro de un proyecto, la guía correspondiente a su Solución + ERP aparece automáticamente arriba de todo — acá es solo para consultar el catálogo completo sin entrar a un proyecto puntual.</span>
        </div>

        {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>}

        {loading ? (
          <div className="p-10 text-center text-gray-400 flex flex-col items-center gap-3">
            <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
            <p className="text-sm">Cargando guías...</p>
          </div>
        ) : guias.length === 0 ? (
          <div className="p-10 text-center text-gray-500 bg-white/5 border border-white/10 rounded-2xl">
            <BookOpenCheck className="w-8 h-8 mx-auto mb-2 text-gray-600" />
            <p className="text-sm">Todavía no hay guías cargadas. Un Admin puede crearlas desde /admin.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(porSolucion).map(([solucion, lista]) => (
              <div key={solucion}>
                <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-2">{labelSolucion(solucion)}</h3>
                <div className="space-y-2">
                  {lista.map((guia) => <GuiaCard key={guia.id} guia={guia} />)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
